/**
 * Static i18n dictionary for the embedded admin app.
 * Supported locales: pt (Portuguese), en (English).
 * Spanish stores (es) map to English.
 */

type Dict = Record<string, string>;

const pt: Dict = {
  // Nav
  'nav.catalog': 'Produtos',
  'nav.settings': 'Configuracoes',

  // Catalog
  'catalog.title': 'Produtos',
  'catalog.subtitle': 'Selecione um produto para gerar fotos com modelo',
  'catalog.empty.title': 'Nenhum produto encontrado',
  'catalog.empty.text': 'Sua loja ainda nao possui produtos cadastrados.',
  'catalog.col.image': 'Imagem',
  'catalog.col.name': 'Nome',
  'catalog.col.action': 'Acao',
  'catalog.btn.enhance': 'Gerar foto',
  'catalog.select.all': 'Todos',
  'catalog.theme.title': 'Provador virtual na sua loja',
  'catalog.theme.body':
    'O botao de provador aparece na pagina de produto apenas em temas compativeis (como o Patagonia). Abra a pagina de um produto na sua loja para confirmar. Se o botao nao aparecer, seu tema ainda nao suporta o recurso.',

  // Enhance
  'enhance.title': 'Gerar foto com modelo',
  'enhance.subtitle': 'Escolha a modelo e o cenario',
  'enhance.section.models': 'Modelos',
  'enhance.section.backgrounds': 'Cenarios',
  'enhance.btn.generate': 'Gerar',
  'enhance.btn.generating': 'Gerando...',
  'enhance.no.credits': 'Creditos insuficientes',
  'enhance.no.credits.msg':
    'Voce nao possui creditos suficientes para gerar uma nova foto. Adquira mais creditos nas configuracoes.',
  'enhance.select.model': 'Selecione uma modelo',
  'enhance.select.background': 'Selecione um cenario',
  'enhance.error.load': 'Erro ao carregar opcoes. Tente novamente.',

  // Progress
  'progress.title': 'Gerando foto',
  'progress.subtitle': 'Aguarde enquanto processamos sua imagem...',
  'progress.status.pending': 'Aguardando processamento',
  'progress.status.processing': 'Processando',
  'progress.status.done': 'Concluido',
  'progress.status.failed': 'Falhou',
  'progress.error': 'Erro ao gerar foto. Tente novamente.',
  'progress.btn.retry': 'Tentar novamente',
  'progress.btn.back': 'Voltar ao produto',

  // Review
  'review.title': 'Foto gerada',
  'review.subtitle': 'Sua nova foto com modelo esta pronta',
  'review.label.original': 'Original',
  'review.label.result': 'Com modelo',
  'review.btn.back': 'Voltar ao catalogo',
  'review.btn.another': 'Gerar outra foto',

  // Settings
  'settings.title': 'Configuracoes',
  'settings.subtitle': 'Informacoes da sua loja',
  'settings.store.name': 'Nome da loja',
  'settings.store.id': 'ID da loja',
  'settings.store.country': 'Pais',
  'settings.credits.title': 'Creditos disponíveis',
  'settings.credits.label': 'Creditos',
  'settings.plan.title': 'Plano',
  'settings.plan.label': 'Plano atual',

  // Credits badge
  'credits.label': 'Creditos',
  'credits.zero': '0 creditos',

  // Errors
  'error.title': 'Algo deu errado',
  'error.generic': 'Ocorreu um erro inesperado. Tente novamente.',
  'error.btn.retry': 'Tentar novamente',
  'error.btn.home': 'Ir ao inicio',

  // Common
  'common.loading': 'Carregando...',
  'common.connecting': 'Conectando...',
  'common.back': 'Voltar',
  'common.save': 'Salvar',
  'common.cancel': 'Cancelar',
  'common.close': 'Fechar',
  'common.page': 'Pagina',
  'common.of': 'de',
};

const en: Dict = {
  // Nav
  'nav.catalog': 'Products',
  'nav.settings': 'Settings',

  // Catalog
  'catalog.title': 'Products',
  'catalog.subtitle': 'Select a product to generate model photos',
  'catalog.empty.title': 'No products found',
  'catalog.empty.text': 'Your store does not have any products yet.',
  'catalog.col.image': 'Image',
  'catalog.col.name': 'Name',
  'catalog.col.action': 'Action',
  'catalog.btn.enhance': 'Generate photo',
  'catalog.select.all': 'All',
  'catalog.theme.title': 'Storefront virtual try-on',
  'catalog.theme.body':
    'The try-on button shows on product pages only on compatible themes (such as Patagonia). Open a product page in your store to confirm. If the button does not appear, your theme does not support it yet.',

  // Enhance
  'enhance.title': 'Generate model photo',
  'enhance.subtitle': 'Choose the model and background',
  'enhance.section.models': 'Models',
  'enhance.section.backgrounds': 'Backgrounds',
  'enhance.btn.generate': 'Generate',
  'enhance.btn.generating': 'Generating...',
  'enhance.no.credits': 'Insufficient credits',
  'enhance.no.credits.msg':
    'You do not have enough credits to generate a new photo. Purchase more credits in settings.',
  'enhance.select.model': 'Select a model',
  'enhance.select.background': 'Select a background',
  'enhance.error.load': 'Error loading options. Please try again.',

  // Progress
  'progress.title': 'Generating photo',
  'progress.subtitle': 'Please wait while we process your image...',
  'progress.status.pending': 'Waiting to process',
  'progress.status.processing': 'Processing',
  'progress.status.done': 'Completed',
  'progress.status.failed': 'Failed',
  'progress.error': 'Error generating photo. Please try again.',
  'progress.btn.retry': 'Try again',
  'progress.btn.back': 'Back to product',

  // Review
  'review.title': 'Photo generated',
  'review.subtitle': 'Your new model photo is ready',
  'review.label.original': 'Original',
  'review.label.result': 'With model',
  'review.btn.back': 'Back to catalog',
  'review.btn.another': 'Generate another photo',

  // Settings
  'settings.title': 'Settings',
  'settings.subtitle': 'Your store information',
  'settings.store.name': 'Store name',
  'settings.store.id': 'Store ID',
  'settings.store.country': 'Country',
  'settings.credits.title': 'Available credits',
  'settings.credits.label': 'Credits',
  'settings.plan.title': 'Plan',
  'settings.plan.label': 'Current plan',

  // Credits badge
  'credits.label': 'Credits',
  'credits.zero': '0 credits',

  // Errors
  'error.title': 'Something went wrong',
  'error.generic': 'An unexpected error occurred. Please try again.',
  'error.btn.retry': 'Try again',
  'error.btn.home': 'Go home',

  // Common
  'common.loading': 'Loading...',
  'common.connecting': 'Connecting...',
  'common.back': 'Back',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.page': 'Page',
  'common.of': 'of',
};

const dictionaries: Record<string, Dict> = { pt, en };

/** Normalise a store language code to 'pt' | 'en'. Spanish falls back to 'en'. */
export function resolveLocale(
  storeLanguage: string | undefined | null,
): 'pt' | 'en' {
  if (!storeLanguage) {
    return 'pt';
  }
  const lang = storeLanguage.toLowerCase().slice(0, 2);
  if (lang === 'pt') {
    return 'pt';
  }
  return 'en';
}

/** Returns a translation function bound to the given locale. */
export function createT(locale: 'pt' | 'en') {
  const dict = dictionaries[locale] ?? dictionaries.pt;
  return function t(key: string, fallback?: string): string {
    return dict[key] ?? fallback ?? key;
  };
}

export type TFunction = ReturnType<typeof createT>;
